import {useMemo, useState} from 'react';
import {Input, Spin, Table, Tooltip} from 'antd';
import {CopyOutlined, SearchOutlined} from '@ant-design/icons';
import {useMutation} from '@apollo/client';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import moment from 'moment';
import PropTypes from 'prop-types';
import styles from './announcementsList.module.scss';
import deleteImg from 'images/delete.svg';
import editImg from 'images/edit.svg';
import pageNotFound from 'images/pageNotFound.png';
import {DELETE_ANNOUNCEMENT} from 'graphql/mutations/announcement';
import {ConfirmModal} from 'pages/adminOpportunities/stocksListings/ViewOrders/HandleOrders';
import {getUserTimezone} from 'utils/helper';
import {NewAnnouncementButton} from './NewAnnouncementButton';

const STATUS_COPY = {
  active: {label: 'Live', tone: 'live'},
  upcoming: {label: 'Scheduled', tone: 'scheduled'},
  past: {label: 'Expired', tone: 'expired'},
};

const readable = value => (value || '').replaceAll('_', ' ').toLowerCase();

/** Dates carried a day and month only, so "17 September" told you neither the
 *  year nor the time a popup actually goes live. */
const DateCell = ({value}) =>
  value ? (
    <div className={styles.dateCell}>
      <span className={styles.dateDay}>
        {moment(value).format('DD MMM YYYY')}
      </span>
      <span className={styles.dateTime}>{moment(value).format('HH:mm')}</span>
    </div>
  ) : (
    <span className={styles.empty}>—</span>
  );

DateCell.propTypes = {value: PropTypes.string};

/**
 * One presentation for all three announcement tabs.
 *
 * They used to disagree: Active rendered fixed 470×190px cards that could not
 * reflow, while Upcoming and Past shared a table whose columns were narrower
 * than their contents — the link was cut mid-path, "17 September" wrapped
 * across two lines and the actions column fell off the right edge.
 */
export const AnnouncementsList = ({
  announcements,
  loading,
  refetch,
  status,
  operations,
}) => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [copiedId, setCopiedId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const [deleteAnnouncement, {loading: deleting}] = useMutation(
    DELETE_ANNOUNCEMENT,
    {errorPolicy: 'all', onError: error => toast.error(error.message)},
  );

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return announcements;
    return announcements.filter(item =>
      [item?.title, item?.subTitle, item?.type, item?.announcementUrl]
        .filter(Boolean)
        .some(field => field.toLowerCase().includes(term)),
    );
  }, [announcements, search]);

  const handleDelete = () => {
    deleteAnnouncement({variables: {announcementId: pendingDelete?.id}}).then(
      ({data}) => {
        if (data?.deleteAnnouncement?.__typename === 'Error') {
          return toast.error(data.deleteAnnouncement.message);
        }
        toast.success('Announcement deleted');
        setPendingDelete(null);
        refetch?.();
      },
    );
  };

  const copyUrl = record => {
    navigator.clipboard.writeText(record.announcementUrl);
    setCopiedId(record.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const columns = [
    {
      title: 'Announcement',
      dataIndex: 'title',
      key: 'announcement',
      width: 320,
      render: (text, record) => (
        <div className={styles.titleCell}>
          {record?.imageUrl ? (
            <img className={styles.thumb} src={record.imageUrl} alt="" />
          ) : (
            // Most announcements carry no image, and an empty <img> left a
            // broken-icon box in every row of the old table.
            <span className={styles.thumbFallback}>
              {(record?.type || '?').charAt(0)}
            </span>
          )}
          <div className={styles.titleText}>
            <span className={styles.name}>
              {record?.title || <em>Untitled announcement</em>}
            </span>
            {record?.subTitle && (
              <span className={styles.sub}>{record.subTitle}</span>
            )}
            <span className={styles.tags}>
              <span className={styles.tag}>{readable(record?.type)}</span>
              {record?.displayType && (
                <span className={styles.tagGhost}>
                  {readable(record.displayType)}
                </span>
              )}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: () => (
        <span
          className={`${styles.status} ${
            styles[STATUS_COPY[status]?.tone || 'scheduled']
          }`}>
          {STATUS_COPY[status]?.label}
        </span>
      ),
    },
    {
      title: 'Published',
      dataIndex: 'publishedDate',
      key: 'publishedDate',
      width: 140,
      render: value => <DateCell value={value} />,
    },
    {
      title: 'Expires',
      dataIndex: 'expiredDate',
      key: 'expiredDate',
      width: 140,
      render: value => <DateCell value={value} />,
    },
    {
      title: 'Link',
      dataIndex: 'announcementUrl',
      key: 'announcementUrl',
      width: 240,
      render: (text, record) =>
        record?.announcementUrl ? (
          <div className={styles.linkCell}>
            <Tooltip title={record.announcementUrl}>
              <a
                className={styles.link}
                href={record.announcementUrl}
                target="_blank"
                rel="noreferrer">
                {record.announcementUrl}
              </a>
            </Tooltip>
            <button
              type="button"
              className={styles.copy}
              onClick={() => copyUrl(record)}>
              <CopyOutlined />
              {copiedId === record.id ? 'Copied' : 'Copy'}
            </button>
          </div>
        ) : (
          <span className={styles.empty}>No link</span>
        ),
    },
    {
      title: '',
      key: 'actions',
      width: 96,
      fixed: 'right',
      render: (text, record) => (
        <div className={styles.actions}>
          {operations?.update && (
            <Tooltip title="Edit">
              <button
                type="button"
                className={styles.iconButton}
                onClick={() =>
                  navigate(`/dashboard/announcements/${record?.id}`)
                }>
                <img src={editImg} alt="Edit" />
              </button>
            </Tooltip>
          )}
          {operations?.delete && (
            <Tooltip title="Delete">
              <button
                type="button"
                className={`${styles.iconButton} ${styles.danger}`}
                onClick={() => setPendingDelete(record)}>
                <img src={deleteImg} alt="Delete" />
              </button>
            </Tooltip>
          )}
        </div>
      ),
    },
  ];

  if (loading && !announcements.length) {
    return (
      <div className={styles.state}>
        <Spin size="large" />
        <p>Loading announcements…</p>
      </div>
    );
  }

  if (!announcements.length) {
    return (
      <div className={styles.state}>
        <img className={styles.stateArt} src={pageNotFound} alt="" />
        <h2>No {STATUS_COPY[status]?.label.toLowerCase()} announcements</h2>
        <p>
          {status === 'past'
            ? 'Announcements move here once they expire.'
            : 'Create one and it will show up here.'}
        </p>
        <NewAnnouncementButton disabled={!operations?.create} />
      </div>
    );
  }

  return (
    <div className={styles.list}>
      <div className={styles.toolbar}>
        <Input
          allowClear
          className={styles.search}
          value={search}
          onChange={event => setSearch(event.target.value)}
          prefix={<SearchOutlined />}
          placeholder="Search by title, type or link"
        />
        <span className={styles.count}>
          {rows.length} of {announcements.length}
        </span>
      </div>

      <Table
        rowKey="id"
        className={styles.table}
        dataSource={rows}
        columns={columns}
        // Dates sit ahead of the link: when the window is too narrow for
        // every column, the one that scrolls out of view should be the
        // least load-bearing. Narrower than 900 the columns collide, so the
        // table scrolls as one piece with the actions pinned.
        scroll={{x: 900}}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          size: 'small',
          hideOnSinglePage: true,
        }}
      />

      <p className={styles.timezone}>Times shown in {getUserTimezone()}</p>

      <ConfirmModal
        openModal={Boolean(pendingDelete)}
        setOpenModal={() => setPendingDelete(null)}
        title="Delete this announcement?"
        loading={deleting}
        handleOperation={handleDelete}
        confirmText={`"${
          pendingDelete?.title || 'This announcement'
        }" will be removed for everyone. This cannot be undone.`}
      />
    </div>
  );
};

AnnouncementsList.propTypes = {
  announcements: PropTypes.array,
  loading: PropTypes.bool,
  refetch: PropTypes.func,
  status: PropTypes.oneOf(['active', 'upcoming', 'past']).isRequired,
  operations: PropTypes.object,
};

AnnouncementsList.defaultProps = {
  announcements: [],
  loading: false,
  operations: {},
};

export default AnnouncementsList;
